import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import LaunchGate from "@/lib/launch";
import NotFound from "@/pages/NotFound";
import InstallPrompt from "@/components/InstallPrompt";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SiteContentProvider, EditorModePill } from "@/lib/siteContent";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SignupPrompt from "@/components/SignupPrompt";
import VisitTracker from "@/components/VisitTracker";
import ScrollToHash from "@/components/ScrollToHash";
import Home from "@/pages/Home";
import { getDeviceId } from "@/lib/device";
import { CATEGORY_PATHS } from "@/lib/paths";
import BodyUnlock from "@/components/BodyUnlock";
import ErrorBoundary from "@/components/ErrorBoundary";
import MobileTabBar from "@/components/layout/MobileTabBar";

// patch 26: every page except the home page downloads only when it is opened
function lazyPage(load) {
  // after a new deploy an old tab may ask for a file that no longer exists: reload once to get the new version
  return lazy(() =>
    load().then((m) => {
      try {
        sessionStorage.removeItem("tier_chunk_reload");
      } catch {
        /* storage blocked */
      }
      return m;
    }).catch((e) => {
      try {
        if (!sessionStorage.getItem("tier_chunk_reload")) {
          sessionStorage.setItem("tier_chunk_reload", "1");
          window.location.reload();
          return new Promise(() => {});
        }
      } catch {
        /* storage blocked */
      }
      throw e;
    })
  );
}
const Explore = lazyPage(() => import("@/pages/Explore"));
const Category = lazyPage(() => import("@/pages/Category"));
const EntryDetail = lazyPage(() => import("@/pages/EntryDetail"));
const Vote = lazyPage(() => import("@/pages/Vote"));
const HallOfFame = lazyPage(() => import("@/pages/HallOfFame"));
const Features = lazyPage(() => import("@/pages/Features"));
const HowWeRate = lazyPage(() => import("@/pages/HowWeRate"));   // patch 35
const Finder = lazyPage(() => import("@/pages/Finder"));
const Submit = lazyPage(() => import("@/pages/Submit"));
const Register = lazyPage(() => import("@/pages/Register"));
const Login = lazyPage(() => import("@/pages/Login"));
const Recover = lazyPage(() => import("@/pages/Recover"));
const Profile = lazyPage(() => import("@/pages/Profile"));
const AdminLogin = lazyPage(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazyPage(() => import("@/pages/AdminDashboard"));
const LegalPage = lazyPage(() => import("@/pages/LegalPage"));
const Leaderboard = lazyPage(() => import("@/pages/Leaderboard"));
const SealPage = lazyPage(() => import("@/pages/SealPage"));
const ReferralLanding = lazyPage(() => import("@/pages/ReferralLanding"));
const TicketsPage = lazyPage(() => import("@/pages/TicketsPage"));

// patch 30: member-only pop-ups and the ticket form download only when they can be needed
const MemberExtras = lazy(() => import("@/components/MemberExtras"));
const TicketHost = lazy(() => import("@/components/Tickets").then((m) => ({ default: m.TicketHost })));
function MemberLayer() {
  const { user } = useAuth();
  return user ? <Suspense fallback={null}><MemberExtras /></Suspense> : null;
}
function TicketLayer() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const load = () => setOn(true);
    window.addEventListener("tier:ticket-load", load);
    return () => window.removeEventListener("tier:ticket-load", load);
  }, []);
  return on ? <Suspense fallback={null}><TicketHost /></Suspense> : null;
}

// batch 21: /c/stores → /stores (old links and bookmarks keep working)
function OldCategoryRedirect() {
  const { id } = useParams();
  const loc = useLocation();
  return <Navigate to={`/${id}${loc.search}${loc.hash}`} replace />;
}

getDeviceId(); // first visit: create this browser's device ID

// patch 37: a crash on one page shows a small error box (header and menus keep working); a new address clears it
function PageBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <HelmetProvider>
        <I18nProvider>
          <AuthProvider>
            <SiteContentProvider>
            <BrowserRouter>
            <LaunchGate>
              <VisitTracker />
              <ScrollToHash />
              <Header />
              <main className="relative">
                <PageMotion>
                <PageBoundary>
                <Suspense fallback={<div className="min-h-[70vh]" aria-busy="true" />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/explore" element={<Explore />} />
                  <Route path="/c/:id" element={<OldCategoryRedirect />} />
                  <Route path="/e/:id" element={<EntryDetail />} />
                  {CATEGORY_PATHS.map((c) => (
                    <Route key={c} path={`/${c}`} element={<Category key={c} catId={c} />} />
                  ))}
                  {CATEGORY_PATHS.map((c) => (
                    <Route key={`${c}-e`} path={`/${c}/:slug`} element={<EntryDetail catId={c} />} />
                  ))}
                  <Route path="/r/:username" element={<ReferralLanding />} />
                  <Route path="/tickets" element={<TicketsPage />} />
                  <Route path="/u/:username" element={<Profile />} />
                  <Route path="/vote" element={<Vote />} />
                  <Route path="/finder" element={<Finder />} />
                  <Route path="/submit" element={<Submit />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/recover" element={<Recover />} />
                  <Route path="/admin" element={<AdminLogin />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/top" element={<Leaderboard />} />
                  <Route path="/hall-da-fama" element={<HallOfFame />} />
                  <Route path="/funcionalidades" element={<Features />} />
                  <Route path="/como-avaliamos" element={<HowWeRate />} />
                  <Route path="/features" element={<Navigate to="/funcionalidades" replace />} />
                  <Route path="/selo/:id" element={<SealPage />} />
                  <Route path="/about" element={<LegalPage pageKey="about" />} />
                  <Route path="/terms" element={<LegalPage pageKey="terms" />} />
                  <Route path="/privacy" element={<LegalPage pageKey="privacy" />} />
                  <Route path="/contact" element={<LegalPage pageKey="contact" />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                </PageBoundary>
                </PageMotion>
              </main>
              <Footer />
              <MobileTabBar />
              <SignupPrompt />
              <EditorModePill />
              <InstallPrompt />
              <MemberLayer />
              <TicketLayer />
              <BodyUnlock />
            </LaunchGate>
            </BrowserRouter>
            </SiteContentProvider>
          </AuthProvider>
        </I18nProvider>
      </HelmetProvider>
      <Toaster
        position={typeof window !== "undefined" && window.innerWidth < 640 ? "bottom-center" : "bottom-right"}
        theme="dark"
        richColors={false}
        /* patch 37: short and few: the newest replaces older ones instead of a tall stack */
        visibleToasts={2}
        swipeDirections={["right", "bottom", "left"]}
        toastOptions={{
          duration: 3000,
          classNames: {
            toast: "tier-toast",
            success: "tier-toast-ok",
            error: "tier-toast-err",
            info: "tier-toast-info",
            description: "tier-toast-desc",
            actionButton: "tier-toast-action",
          },
        }}
      />
      </ErrorBoundary>
    </div>
  );
}

export default App;

/** Re-plays the page entrance whenever the path changes (query/hash changes don't count). */
function PageMotion({ children }) {
  const { pathname } = useLocation();
  return <div key={pathname} className="tier-page">{children}</div>;
}
